import type { JSX } from "react";

import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import {
    LexicalTypeaheadMenuPlugin,
    MenuOption,
    MenuTextMatch,
} from "@lexical/react/LexicalTypeaheadMenuPlugin";
import { TextNode } from "lexical";
import { useCallback, useMemo, useState } from "react";
import * as React from "react";
import * as ReactDOM from "react-dom";

import { $createDirectiveNode } from "@mdxeditor/editor";
import { Avatar, Paper, SearchTermHighlight } from "../../../../shared/components";
import clsx from "clsx";

export type MentionOption = {
    value: string;
    label: string;
    picture?: string;
    [key: string]: any;
}

const PUNCTUATION = `\\.,\\+\\*\\?\\$\\@\\|#{}\\(\\)\\^\\-\\[\\]\\\\/!%\'"~=<>_:;`; // Valid punctuation characters to include in the search query...
const TRIGGERS = ["@"].join("");

// Chars we expect to see in a mention (non-space, non-punctuation).
const VALID_CHARS = "[^" + TRIGGERS + PUNCTUATION + "\\s]";

// Non-standard series of chars. Each series must be preceded and followed by
// a valid char.
const VALID_JOINS =
    "(?:" +
    "\\.[ |$]|" + // E.g. "r. " in "Mr. Smith"
    " |" + // E.g. " " in "Josh Duck"
    "[" +
    PUNCTUATION +
    "]|" + // E.g. "-' in "Salier-Hellendag"
    ")";

const LENGTH_LIMIT = 75;

const AtSignMentionsRegex = new RegExp(
    "(^|\\s|\\()(" +
    "[" +
    TRIGGERS +
    "]" +
    "((?:" +
    VALID_CHARS +
    VALID_JOINS +
    "){0," +
    LENGTH_LIMIT +
    "})" +
    ")$",
);

// 50 is the longest alias length limit.
const ALIAS_LENGTH_LIMIT = 50;

// Regex used to match alias.
const AtSignMentionsRegexAliasRegex = new RegExp(
    "(^|\\s|\\()(" +
    "[" +
    TRIGGERS +
    "]" +
    "((?:" +
    VALID_CHARS +
    "){0," +
    ALIAS_LENGTH_LIMIT +
    "})" +
    ")$",
);

// At most, 5 suggestions are shown in the popup.
const SUGGESTION_LIST_LENGTH_LIMIT = 5;

function checkForAtSignMentions(
    text: string,
    minMatchLength: number,
): MenuTextMatch | null {
    let match = AtSignMentionsRegex.exec(text);

    if (match === null) {
        match = AtSignMentionsRegexAliasRegex.exec(text);
    }
    if (match !== null) {
        // The strategy ignores leading whitespace but we need to know it"s
        // length to add it to the leadOffset
        const maybeLeadingWhitespace = match[1];

        const matchingString = match[3];
        if (matchingString.length >= minMatchLength) {
            return {
                leadOffset: match.index + maybeLeadingWhitespace.length,
                matchingString,
                replaceableString: match[2],
            };
        }
    }
    return null;
}

function getPossibleQueryMatch(text: string): MenuTextMatch | null {
    return checkForAtSignMentions(text, 0);
}

class MentionTypeaheadOption extends MenuOption {
    label: string;
    value: string;
    picture?: JSX.Element;

    constructor(label: string, value: string, picture?: JSX.Element) {
        super(label);
        this.label = label;
        this.value = value;
        this.picture = picture;
    }
}

type MentionsTypeaheadMenuItemProps = {
    index: number;
    isSelected: boolean;
    onClick: () => void;
    onMouseEnter: () => void;
    option: MentionTypeaheadOption;
    queryString: string;
};

const MentionsTypeaheadMenuItem: React.FC<MentionsTypeaheadMenuItemProps> = ({
    index,
    isSelected,
    onClick,
    onMouseEnter,
    option,
    queryString,
}) => (
    <li
        key={option.key}
        tabIndex={-1}
        className={clsx("MuiAutocomplete-option mention-menu-item", isSelected && "selected")}
        ref={option.setRefElement}
        role="option"
        aria-selected={isSelected}
        id={"typeahead-item-" + index}
        onMouseEnter={onMouseEnter}
        onClick={onClick}
    >
        {option.picture}
        <SearchTermHighlight text={option.label} searchTerm={queryString} className="text" />
    </li>
);

export type MentionCommandMenuProps = {
    options: MentionOption[];
};

const MentionCommandMenu: React.FC<MentionCommandMenuProps> = ({ options }): JSX.Element | null => {
    const [editor] = useLexicalComposerContext();
    const [queryString, setQueryString] = useState<string | null>(null);

    const matchingOptions = useMemo(() =>
            options
                .filter(o => o.label.toLowerCase().startsWith(queryString?.toLowerCase() ?? ""))
                .map(result =>
                    new MentionTypeaheadOption(
                        result.label,
                        result.value,
                        <Avatar className="small" alt={result.label} src={result?.picture} fallback={result.label} />
                    )
                )
                .slice(0, SUGGESTION_LIST_LENGTH_LIMIT)
            , [options, queryString]);

    const onSelectOption = useCallback(
        (
            selectedOption: MentionTypeaheadOption,
            nodeToReplace: TextNode | null,
            closeMenu: () => void,
        ) => {
            editor.update(() => {
                const mentionNode = $createDirectiveNode({
                    type: "textDirective",
                    name: "mention",
                    attributes: { value: selectedOption.value },
                    children: [{ "type": "text", value: selectedOption.label }]
                });
                if (nodeToReplace) {
                    nodeToReplace.replace(mentionNode);
                }
                mentionNode.select();
                closeMenu();
            });
        },
        [editor],
    );

    return (
        <LexicalTypeaheadMenuPlugin<MentionTypeaheadOption>
            onQueryChange={setQueryString}
            onSelectOption={onSelectOption}
            triggerFn={getPossibleQueryMatch}
            options={matchingOptions}
            menuRenderFn={(
                anchorElementRef,
                { selectedIndex, selectOptionAndCleanUp, setHighlightedIndex },
            ) =>
                anchorElementRef.current && matchingOptions.length
                    ? ReactDOM.createPortal(
                        <Paper elevation={1} className="mentions-menu">
                            <ul className="MuiAutocomplete-listbox">
                                {matchingOptions.map((option, i: number) => (
                                    <MentionsTypeaheadMenuItem
                                        index={i}
                                        isSelected={selectedIndex === i}
                                        onClick={() => {
                                            setHighlightedIndex(i);
                                            selectOptionAndCleanUp(option);
                                        }}
                                        onMouseEnter={() => {
                                            setHighlightedIndex(i);
                                        }}
                                        key={option.key}
                                        option={option}
                                        queryString={queryString}
                                    />
                                ))}
                            </ul>
                        </Paper>,
                        anchorElementRef.current,
                    ) as any
                    : null
            }
        />
    );
};

export { MentionCommandMenu };